import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { agentById } from '../data/seedData.js';

/**
 * RoleContext — app-wide source of truth for the current demo role and the
 * persona behind it. In production this will be replaced by the decoded JWT;
 * for demos we expose a simple switcher in the sidebar.
 *
 * Defaults per CLAUDE.md seed rules:
 *   team_leader → Marco Rivera    (sees everything)
 *   agent       → Ana Castillo    (so she has assigned leads to show)
 *   isa         → Camila Reyes    (handles follow-ups and assignments)
 */

const ROLES = ['team_leader', 'agent', 'isa'];

const DEFAULT_USER_BY_ROLE = {
  team_leader: 'agent-marco-rivera',
  agent: 'agent-ana-castillo',
  isa: 'agent-camila-reyes',
};

const STORAGE_KEY = 're_dev_role';

const RoleContext = createContext(null);

function readInitialRole() {
  if (typeof window === 'undefined') return 'team_leader';
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && ROLES.includes(saved)) return saved;
  } catch {
    /* ignore */
  }
  return 'team_leader';
}

export function RoleProvider({ children }) {
  const [role, setRoleState] = useState(readInitialRole);

  const setRole = useCallback((next) => {
    if (!ROLES.includes(next)) return;
    setRoleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(() => {
    const userId = DEFAULT_USER_BY_ROLE[role];
    const currentUser = agentById[userId] || null;
    return { role, setRole, currentUser, availableRoles: ROLES };
  }, [role, setRole]);

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) {
    throw new Error('useRole must be used inside <RoleProvider>');
  }
  return ctx;
}
